import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Github, Linkedin, Mail, Phone, MapPin, Download, ExternalLink, ArrowRight } from 'lucide-react';

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [openerActive, setOpenerActive] = useState(false);
  const [openerTitle, setOpenerTitle] = useState('');
  const [robotPos, setRobotPos] = useState('bottom-right'); // 'bottom-right' | 'hero-top-left'
  const openerOverlayRef = useRef(null);
  const robotRef = useRef(null);
  const robotYRef = useRef(0);
  const robotTargetYRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      const sections = ['home', 'about', 'projects', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Preloader: show robot wave with blur effect, then hide and move robot to hero top-left
  useEffect(() => {
    // simulate page load or wait for real load event
    const onLoad = () => {
      // Apply blur effect to html element
      document.documentElement.classList.add('preloader-active');
      
      // show hero opener first as minimal welcome
      setOpenerTitle('Hello Me');
      setOpenerActive(true);
      if (openerOverlayRef.current) {
        openerOverlayRef.current.classList.remove('hide-overlay');
        openerOverlayRef.current.classList.add('show-overlay');
      }

      // hide opener after 1 second display
      setTimeout(() => {
        if (openerOverlayRef.current) openerOverlayRef.current.classList.add('hide-overlay');
      }, 1000);

      // Remove blur effect and hide preloader
      setTimeout(() => {
        document.documentElement.classList.remove('preloader-active');
        setOpenerActive(false);
        // hide preloader after opener
        setIsLoading(false);
        // after hiding loader, move robot to hero top-left
        setTimeout(() => setRobotPos('hero-top-left'), 300);
      }, 2000);
    };

    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad);

    return () => {
      window.removeEventListener('load', onLoad);
      document.documentElement.classList.remove('preloader-active');
    };
  }, []);

  // Generic intersection observer for section openers
  useEffect(() => {
    const sections = ['about', 'projects', 'contact', 'home'];
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !isLoading) {
          const id = entry.target.id || '';
          const title = id === 'about' ? 'About Me' : id.charAt(0).toUpperCase() + id.slice(1);
          // trigger opener overlay
          setOpenerTitle(title);
          setOpenerActive(true);
          if (openerOverlayRef.current) {
            openerOverlayRef.current.classList.remove('hide-overlay');
            openerOverlayRef.current.classList.add('show-overlay');
          }
          // display for 2 seconds then hide
          setTimeout(() => {
            if (openerOverlayRef.current) openerOverlayRef.current.classList.add('hide-overlay');
          }, 2000);
          setTimeout(() => setOpenerActive(false), 2600);
        }
      });
    }, { threshold: 0.3 });

    sections.forEach(s => {
      const el = document.getElementById(s);
      if (el) io.observe(el);
    });

    return () => io.disconnect();
  }, [isLoading]);

  // Robot scrolling behavior + random popups
  useEffect(() => {
    const robot = robotRef.current;
    if (!robot) return;

    // smooth follow using requestAnimationFrame for inertia
    const onScroll = () => {
      const y = window.scrollY;
      robotTargetYRef.current = Math.min(120, y * 0.04);
    };

    const loop = () => {
      const current = robotYRef.current;
      const target = robotTargetYRef.current;
      const next = current + (target - current) * 0.12; // ease
      robotYRef.current = next;
      robot.style.transform = `translateY(${next}px)`;
      rafRef.current = requestAnimationFrame(loop);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    rafRef.current = requestAnimationFrame(loop);

    // random popups every 8-14s
    let popupTimer = null;
    const schedule = () => {
      const delay = 8000 + Math.random() * 6000;
      popupTimer = setTimeout(() => {
        robot.classList.add('robot-pop');
        setTimeout(() => robot.classList.remove('robot-pop'), 2200);
        schedule();
      }, delay);
    };
    schedule();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (popupTimer) clearTimeout(popupTimer);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! I will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const skills = [
    { name: 'HTML/CSS', level: 95 },
    { name: 'JavaScript', level: 70 },
    { name: 'PHP', level: 90 },
    { name: 'Laravel', level: 85 },
    { name: 'MySQL', level: 80 },
    { name: 'jQuery/Ajax', level: 70 },
    { name: 'CodeIgniter', level: 70 },
    { name: 'Git/GitHub', level: 60 }
  ];

  const projects = [
    { title: 'ERP System', category: 'Laravel', image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop', link: '#', desc: 'Enterprise Resource Planning system with comprehensive modules' },
    { title: 'Event Management', category: 'Laravel', image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=300&fit=crop', link: 'https://bridalharmony.com.bd/', desc: 'Full-featured event booking and management platform' },
    { title: 'E-Commerce Platform', category: 'Laravel', image: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=400&h=300&fit=crop', link: 'https://github.com/Leaya0214/Ecommerce-website', desc: 'Scalable e-commerce solution with payment integration' },
    { title: 'HRM System', category: 'CodeIgniter', image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop', link: '#', desc: 'Human Resource Management with payroll automation' },
    { title: 'POS Software', category: 'CodeIgniter', image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop', link: '#', desc: 'Point of Sale system for retail operations' },
    { title: 'BRRI VMS', category: 'Laravel', image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop', link: '#', desc: 'Visitor Management System for institutions' }
  ];

  const splitTitle = (title) => {
    if (!title) return { left: '', right: '' };
    const parts = title.split(' ');
    if (parts.length > 1) {
      return { left: parts.slice(0, parts.length - 1).join(' '), right: parts.slice(-1).join(' ') };
    }
    const mid = Math.ceil(title.length / 2);
    return { left: title.slice(0, mid), right: title.slice(mid) };
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <button onClick={() => scrollToSection('home')} className="text-2xl font-bold text-white tracking-tight hover:text-gray-300 transition-colors duration-300">
              leaya<span className="text-cyan-400">.</span>
            </button>

            {/* Desktop Menu - Centered */}
            <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2">
              <div className="flex space-x-12">
                {['home', 'about', 'projects', 'contact'].map(item => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className={`menu-item capitalize text-sm font-medium transition-all duration-300 relative group ${activeSection === item ? 'text-cyan-400' : 'text-gray-300 hover:text-white'}`}
                  >
                    {item}
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-300 ${activeSection === item ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                  </button>
                ))}
              </div>
            </div>

            {/* Social Links - Desktop */}
            <div className="hidden md:flex space-x-6">
              <a href="https://github.com/Leaya0214" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:scale-110">
                <Github size={20} />
              </a>
              <a href="https://www.linkedin.com/in/leaya-sultana-74b1a21b4/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:scale-110">
                <Linkedin size={20} />
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button className="md:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-black/90 backdrop-blur-md border-t border-gray-700">
              {['home', 'about', 'projects', 'contact'].map(item => (
                <button key={item} onClick={() => scrollToSection(item)} className="menu-item block w-full text-left px-6 py-4 capitalize text-sm text-gray-300 hover:text-cyan-400 hover:bg-white/5 transition-colors">
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Global section opener overlay (fixed) */}
      <div ref={openerOverlayRef} className="opener-overlay pointer-events-none fixed inset-0 flex items-center justify-center z-50 opacity-0">
        <div className="opener-inner flex items-center gap-6">
          {(() => {
            const { left, right } = splitTitle(openerTitle);
            return (
              <>
                <span className="opener-left text-[42px] md:text-6xl font-extrabold tracking-tight text-white">{left}</span>
                <span className="opener-right text-[42px] md:text-6xl font-extrabold tracking-tight text-white">{right}</span>
              </>
            );
          })()}
        </div>
      </div>

      {/* Corner decorations */}
      <div className="corner-deco left">
        <div className="line"></div>
        <div className="box"></div>
      </div>
      <div className="corner-deco right">
        <div className="line"></div>
        <div className="box"></div>
      </div>

      {/* Preloader overlay with waving robot */}
      {isLoading && (
        <div className="preloader-overlay fixed inset-0 z-60 flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="mb-6">
              <svg width="120" height="120" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="robot-svg robot-wave">
                <rect x="10" y="20" width="60" height="40" rx="8" fill="#071028" stroke="#22d3ee" strokeWidth="1" />
                <rect x="24" y="10" width="32" height="16" rx="4" fill="#071028" stroke="#22d3ee" strokeWidth="1" />
                <circle cx="30" cy="36" r="3" fill="#22d3ee" />
                <circle cx="50" cy="36" r="3" fill="#22d3ee" />
                <g className="arm">
                  <rect x="62" y="28" width="8" height="3" rx="1" fill="#22d3ee" />
                </g>
              </svg>
            </div>
            <div className="text-cyan-400 font-medium">Hello <span className="wave">👋</span></div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-4 relative overflow-hidden bg-black">
        {/* Animated gradient background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl opacity-40 -mr-48 -mt-48 animate-pulse"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl opacity-40 -ml-48 -mb-48 animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>

        <div className="max-w-4xl mx-auto text-center z-10">
          <div className="mb-8 animate-fade-in">
            <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-8 border-2 border-cyan-400/50 shadow-lg shadow-cyan-400/20 hover:shadow-cyan-400/40 transition-all duration-500">
              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="Profile" className="w-full h-full object-cover" />
            </div>
          </div>
          
          <div className="mb-8">
            <p className="text-cyan-400 text-xs font-semibold uppercase tracking-[0.15em] mb-6 animate-slide-up" style={{animationDelay: '0.1s'}}>Full-Stack Developer</p>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight animate-slide-up" style={{animationDelay: '0.2s'}}>
              I'm <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">Leaya</span>
            </h1>
          </div>
          
          <p className="text-lg md:text-xl text-gray-300 font-medium mb-4 animate-slide-up" style={{animationDelay: '0.3s'}}>
            Building Scalable Web Solutions
          </p>
          
          <p className="text-sm md:text-base text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-light animate-slide-up" style={{animationDelay: '0.4s'}}>
            2+ years of professional experience designing robust backend systems, architecting enterprise solutions, and crafting elegant code that scales.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-slide-up" style={{animationDelay: '0.5s'}}>
            <button onClick={() => scrollToSection('projects')} className="px-8 py-3.5 bg-white text-black rounded-lg shadow-lg shadow-white/10 hover:shadow-xl hover:shadow-white/20 transition-all duration-300 font-semibold text-sm flex items-center justify-center gap-2 hover:scale-105 group">
              View My Work <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => scrollToSection('contact')} className="px-8 py-3.5 border border-white/20 text-white rounded-lg hover:border-white/50 hover:bg-white/5 transition-all duration-300 font-semibold text-sm">
              Get In Touch
            </button>
          </div>

          <div className="flex gap-4 justify-center md:hidden animate-slide-up" style={{animationDelay: '0.6s'}}>
            <a href="https://github.com/Leaya0214" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-colors border border-gray-600 rounded-lg hover:border-cyan-400/50">
              <Github size={20} />
            </a>
            <a href="https://www.linkedin.com/in/leaya-sultana-74b1a21b4/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-colors border border-gray-600 rounded-lg hover:border-cyan-400/50">
              <Linkedin size={20} />
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cyan-400/50 rounded-full flex items-center justify-center">
            <div className="w-1 h-2 bg-cyan-400/50 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-4 bg-gradient-to-b from-black to-gray-950 border-t border-gray-800 relative overflow-hidden">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            About <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Me</span>
          </h2>
          <p className="text-lg text-gray-400 mb-16 font-light">Getting to know the developer behind the code</p>

          <div className="grid md:grid-cols-2 gap-16 mb-16">
            <div>
              <p className="text-lg text-gray-300 font-light mb-6 leading-relaxed">
                I'm a passionate full-stack developer with a strategic focus on backend architecture. Since 2022, I've been crafting robust systems that power real businesses and serve thousands of users.
              </p>
              <p className="text-lg text-gray-300 font-light mb-6 leading-relaxed">
                My expertise spans across complex domains — from enterprise resource planning to e-commerce platforms. I thrive on solving intricate technical problems and optimizing system performance for scale.
              </p>
              <p className="text-lg text-gray-300 font-light leading-relaxed">
                Clean code, architectural patterns, and continuous learning are at the core of my development philosophy. I'm always exploring new technologies and best practices to deliver better solutions.
              </p>
            </div>

            <div className="space-y-8">
              <div className="bg-gradient-to-r from-cyan-400/10 to-blue-500/10 border border-cyan-400/20 rounded-lg p-8 hover:border-cyan-400/50 transition-all duration-300">
                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Currently</h3>
                <p className="text-2xl text-white font-semibold mb-1">Web Developer at STITBD</p>
                <p className="text-sm text-gray-400">August 2023 - Present</p>
              </div>

              <div className="bg-gradient-to-r from-cyan-400/10 to-blue-500/10 border border-cyan-400/20 rounded-lg p-8 hover:border-cyan-400/50 transition-all duration-300">
                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Education</h3>
                <p className="text-2xl text-white font-semibold mb-1">BSc in Computer Science</p>
                <p className="text-sm text-gray-400">National University, 2022</p>
              </div>

              <div className="bg-gradient-to-r from-cyan-400/10 to-blue-500/10 border border-cyan-400/20 rounded-lg p-8 hover:border-cyan-400/50 transition-all duration-300">
                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Experience</h3>
                <p className="text-2xl text-white font-semibold mb-1">2+ Years Professional</p>
                <p className="text-sm text-gray-400">Full-Stack Web Development</p>
              </div>

              <button className="w-full px-6 py-4 bg-gradient-to-r from-cyan-400 to-blue-500 text-black rounded-lg hover:shadow-lg hover:shadow-cyan-400/50 transition-all duration-300 font-semibold flex items-center justify-center gap-2 hover:scale-105">
                <Download size={20} />
                Download CV
              </button>
            </div>
          </div>

          {/* Skills Grid */}
          <div className="pt-16 border-t border-gray-800">
            <h3 className="text-3xl font-bold text-white mb-12">Technical <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Expertise</span></h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { skill: 'Laravel', level: '95%' },
                { skill: 'PHP', level: '90%' },
                { skill: 'MySQL', level: '80%' },
                { skill: 'HTML/CSS', level: '95%' },
                { skill: 'JavaScript', level: '70%' },
                { skill: 'CodeIgniter', level: '70%' },
                { skill: 'Git/GitHub', level: '60%' },
                { skill: 'API Design', level: '85%' }
              ].map((item, idx) => (
                <div key={idx} className="bg-gray-900/50 border border-gray-800 hover:border-cyan-400/30 p-5 rounded-lg transition-all duration-300 group hover:bg-gray-800/50">
                  <p className="text-sm text-gray-300 font-medium mb-3 group-hover:text-cyan-400 transition-colors">{item.skill}</p>
                  <div className="w-full bg-gray-700 rounded-full h-1.5">
                    <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-1.5 rounded-full transition-all duration-1000" style={{ width: item.level }}></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">{item.level}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 px-4 bg-black border-t border-gray-800">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Featured <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Projects</span>
          </h2>
          <p className="text-lg text-gray-400 font-light mb-16">Transforming ideas into scalable solutions</p>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <a key={index} href={project.link} target={project.link !== '#' ? '_blank' : ''} rel="noopener noreferrer" className="group bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden hover:border-cyan-400/50 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-400/10">
                <div className="relative overflow-hidden h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="text-center">
                      <ExternalLink size={40} className="text-cyan-400 mb-2 mx-auto" />
                      <p className="text-white text-sm font-medium">View Project</p>
                    </div>
                  </div>
                </div>
                <div className="p-8">
                  <span className="text-xs font-semibold text-cyan-400 uppercase tracking-widest bg-cyan-400/10 px-3 py-1 rounded-full inline-block mb-4">{project.category}</span>
                  <h3 className="text-2xl font-bold text-white mt-2 mb-3 group-hover:text-cyan-400 transition-colors">{project.title}</h3>
                  <p className="text-sm text-gray-400 font-light leading-relaxed">{project.desc}</p>
                  <div className="mt-4 flex items-center text-cyan-400 text-sm font-medium group-hover:translate-x-2 transition-transform">
                    Learn more <ArrowRight size={16} className="ml-2" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 bg-gradient-to-b from-black to-gray-950 border-t border-gray-800">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 text-center">
            Let's <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Connect</span>
          </h2>
          <p className="text-lg text-gray-400 font-light text-center mb-16">Have an exciting project or just want to discuss ideas?</p>

          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <a href="mailto:leayasultana@gmail.com" className="group bg-gray-900/50 border border-gray-800 p-8 rounded-lg hover:border-cyan-400/50 hover:bg-gray-900 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/10">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:from-cyan-400/40 group-hover:to-blue-500/40 transition-all">
                <Mail className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">Email</h3>
              <p className="text-sm text-gray-400 font-light truncate group-hover:text-gray-300 transition-colors">leayasultana@gmail.com</p>
            </a>

            <a href="tel:+8801732369984" className="group bg-gray-900/50 border border-gray-800 p-8 rounded-lg hover:border-cyan-400/50 hover:bg-gray-900 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/10">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:from-cyan-400/40 group-hover:to-blue-500/40 transition-all">
                <Phone className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">Phone</h3>
              <p className="text-sm text-gray-400 font-light group-hover:text-gray-300 transition-colors">+880 1732 369984</p>
            </a>

            <div className="bg-gray-900/50 border border-gray-800 p-8 rounded-lg">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-6">
                <MapPin className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">Location</h3>
              <p className="text-sm text-gray-400 font-light">Dhaka, Bangladesh</p>
            </div>
          </div>

          <div className="bg-gray-900/50 border border-gray-800 p-12 rounded-xl backdrop-blur">
            <h3 className="text-2xl font-bold text-white mb-8">Send me a message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <input 
                  type="text" 
                  placeholder="Your Name" 
                  value={formData.name} 
                  onChange={(e) => setFormData({...formData, name: e.target.value})} 
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-transparent text-white placeholder-gray-500 font-light transition-all"
                  required
                />
                <input 
                  type="email" 
                  placeholder="Your Email" 
                  value={formData.email} 
                  onChange={(e) => setFormData({...formData, email: e.target.value})} 
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-transparent text-white placeholder-gray-500 font-light transition-all"
                  required
                />
              </div>
              <textarea 
                rows={5} 
                placeholder="Your Message" 
                value={formData.message} 
                onChange={(e) => setFormData({...formData, message: e.target.value})} 
                className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-transparent text-white placeholder-gray-500 font-light transition-all resize-none"
                required
              ></textarea>
              <button type="submit" className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-black py-4 rounded-lg hover:shadow-lg hover:shadow-cyan-400/50 transition-all duration-300 font-semibold hover:scale-105 transform">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Robot helper (floating) */}
      <div
        ref={robotRef}
        className={`site-robot fixed z-50 opacity-95 ${isLoading ? 'robot-center-load robot-wave' : robotPos === 'hero-top-left' ? 'robot-hero-left' : 'robot-bottom-right'}`}
      >
        {/* try to use attached image at /robot.png (place the provided image in `public/robot.png`) */}
        <img src="/robot.png" alt="robot" className="robot-img" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        {/* fallback inline svg if image not available */}
        <svg width="84" height="84" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="robot-svg">
          <rect x="8" y="20" width="64" height="40" rx="8" fill="#071028" stroke="#06b6d4" strokeWidth="1" />
          <rect x="22" y="10" width="36" height="16" rx="4" fill="#071028" stroke="#06b6d4" strokeWidth="1" />
          <circle cx="32" cy="36" r="3" fill="#22d3ee" />
          <circle cx="48" cy="36" r="3" fill="#22d3ee" />
          <g className="arm">
            <rect x="58" y="30" width="10" height="4" rx="2" fill="#22d3ee" />
          </g>
        </svg>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-16 px-4 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div>
              <p className="text-2xl font-bold text-white tracking-tight">leaya<span className="text-cyan-400">.</span></p>
              <p className="text-gray-400 font-light text-sm mt-2">Full-Stack Developer</p>
            </div>
            <div className="flex gap-8">
              <a href="https://github.com/Leaya0214" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 hover:scale-125 transform">
                <Github size={24} />
              </a>
              <a href="https://www.linkedin.com/in/leaya-sultana-74b1a21b4/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors duration-300 hover:scale-125 transform">
                <Linkedin size={24} />
              </a>
            </div>
            <p className="text-gray-400 font-light text-sm">© 2025 Leaya. Crafted with precision</p>
          </div>
        </div>
      </footer>
    </div>
  );
};


export default Portfolio;